import React, { useState } from 'react';
import { ArrowLeft, Building2, Target, Mail, Phone, Package, CalendarDays, StickyNote, Pencil } from 'lucide-react';
import { Avatar, Badge, fmtDate } from '../components/ui.jsx';
import DealModal from '../components/DealModal.jsx';
import { accountForName, contactsFor, stageColor, userById, money } from '../data.js';

const fmtNoteTime = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

function NoteComposer({ currentUser, onAddNote }) {
  const [text, setText] = useState('');
  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onAddNote(trimmed);
    setText('');
  };
  return (
    <div className="note-composer">
      <Avatar name={currentUser?.name} color={currentUser?.color} size="sm" />
      <div className="note-composer-input">
        <textarea
          rows={2}
          placeholder="Log an update — a call, a next step, a risk..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
        />
        <div className="note-composer-actions"><button className="btn btn-primary" disabled={!text.trim()} onClick={submit}>Add note</button></div>
      </div>
    </div>
  );
}

export default function DealDetail({ deal, currentUser, onAddNote, onBack, onOpenAccount, onUpdateDeal, accounts = [], contacts = [], team = [], products = [] }) {
  const [editing, setEditing] = useState(false);
  const account = deal.accountId ? accountForName(accounts, deal.account) : null;
  const dealContacts = account ? contactsFor(contacts, account.id) : [];
  const notes = deal.notes || [];

  return (
    <div>
      <button className="record-back" onClick={onBack}><ArrowLeft size={15} /> Back to pipeline</button>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 }}>
          <div style={{ display: 'flex', gap: 14 }}>
            <Avatar name={deal.account} color={deal.tone} size="lg" />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 700 }}>{deal.account}</span>
                <Badge tone={stageColor[deal.stage]}>{deal.stage}</Badge>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Building2 size={13} /> {(deal.products || []).join(', ')} · Sourced via {deal.source}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="btn btn-primary" onClick={() => setEditing(true)}><Pencil size={13} /> Edit opportunity</button>
                {account && <button className="btn btn-ghost" onClick={() => onOpenAccount(account)}>View full account <ArrowLeft size={12} style={{ transform: 'rotate(180deg)' }} /></button>}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Win probability</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{deal.probability}%</div>
          </div>
        </div>
      </div>

      <section className="detail-grid">
        <div className="card">
          <div className="section-title"><div><Target size={16} /><h3>Opportunity details</h3></div></div>
          <div className="field-grid">
            <div><small>Deal value</small><b>{money(deal.value)}</b></div>
            <div><small>Probability</small><b>{deal.probability}%</b></div>
            <div><small>Products</small><b><Package size={13} />{(deal.products || []).join(', ')}</b></div>
            <div><small>Expected close</small><b><CalendarDays size={13} />{fmtDate(deal.close)}</b></div>
            <div><small>Lead source</small><b>{deal.source}</b></div>
            <div><small>Owner</small><b><Avatar name={userById(team, deal.owner)?.name} size="sm" />{userById(team, deal.owner)?.name}</b></div>
          </div>
        </div>
        <div className="card">
          <div className="section-title"><div><Mail size={16} /><h3>Primary contact</h3></div></div>
          <div className="field-grid">
            <div><small>Name</small><b>{deal.contact}</b></div>
            {account && <div><small>Account</small><b>{account.name}</b></div>}
          </div>
        </div>
      </section>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="section-title"><div><StickyNote size={16} /><h3>Notes & updates</h3></div>{notes.length > 0 && <em>{notes.length}</em>}</div>
        <NoteComposer currentUser={currentUser} onAddNote={onAddNote} />
        {notes.length > 0 ? notes.map((n) => {
          const author = userById(team, n.authorId);
          return (
            <div key={n.id} className="activity-item">
              <Avatar name={author?.name} color={author?.color} size="sm" />
              <div className="activity-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 12.5 }}>{author?.name || 'Unknown'}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{fmtNoteTime(n.createdAt)}</span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3, whiteSpace: 'pre-wrap' }}>{n.text}</div>
              </div>
            </div>
          );
        }) : <div className="table-empty">No notes yet — log the first update.</div>}
      </div>

      {dealContacts.length > 0 && (
        <div className="card">
          <div className="card-header"><div><div className="card-title">Other contacts at {deal.account}</div></div></div>
          {dealContacts.map((c) => (
            <div key={c.id} className="activity-item" style={{ alignItems: 'center' }}>
              <Avatar name={c.name} size="sm" />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 12.5 }}>{c.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{c.role}</div>
              </div>
              <a style={{ fontSize: 12, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 5 }}><Mail size={13} />{c.email}</a>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <DealModal
          deal={deal}
          accounts={accounts}
          contacts={contacts}
          team={team}
          products={products}
          onClose={() => setEditing(false)}
          onSave={(form) => { onUpdateDeal(deal.id, form); setEditing(false); }}
        />
      )}
    </div>
  );
}
